import { useState, useEffect, useCallback } from 'react'
import { X, ChevronLeft, ChevronRight, Heart, Loader2 } from '@/ui/icons'
import { Button } from '@/ui/components'
import { CommentSection } from './CommentSection'
import { Photo } from '@/shared/api/photos'
import { PhotoComment, PhotoInteractionsApi } from '@/shared/api'
import { usePhotoLikesRealtime } from '../hooks/usePhotoLikesRealtime'
import { usePhotoCommentsRealtime } from '../hooks/usePhotoCommentsRealtime'
import { useAuth } from '@/modules/auth'
import { cn } from '@/shared/utils'

/**
 * Get optimized full-size URL for lightbox view
 * Uses .webp versions created by Lambda optimization when available
 * Only uses .webp when optimization is completed, otherwise uses original
 */
function getOptimizedFullSizeUrl(url: string, optimizationStatus?: string): string {
  if (!url || !url.includes('supabase.co')) {
    return url
  }
  
  // Only use .webp if optimization is completed
  // Otherwise, use original URL so user sees image immediately
  if (optimizationStatus !== 'completed') {
    return url
  }
  
  try {
    const urlObj = new URL(url)
    const pathname = urlObj.pathname
    
    // Replace image extensions with .webp (handles jpg, jpeg, png, heic, heif)
    // This uses the optimized .webp files created by AWS Lambda
    const webpPathname = pathname.replace(/\.(jpg|jpeg|png|heic|heif)$/i, '.webp')
    
    // Only use .webp if the extension was actually replaced (i.e., it was an image)
    if (webpPathname !== pathname) {
      urlObj.pathname = webpPathname
    }
    
    // Remove any query params (Supabase Storage doesn't support on-the-fly transformations)
    urlObj.search = ''
    
    return urlObj.toString()
  } catch {
    return url
  }
}

interface PhotoLightboxProps {
  photo: Photo
  photos: Photo[]
  currentIndex: number
  onClose: () => void
  onNavigate?: (index: number) => void
  photoInteractionsApi: PhotoInteractionsApi
  currentUserId?: string
  className?: string
  onSetCoverPhoto?: (photoId: string) => Promise<void>
  coverPhotoId?: string | null
}

export function PhotoLightbox({
  photo,
  photos,
  currentIndex,
  onClose,
  onNavigate,
  photoInteractionsApi,
  currentUserId,
  className,
  onSetCoverPhoto,
  coverPhotoId
}: PhotoLightboxProps) {
  const [comments, setComments] = useState<PhotoComment[]>([])
  const [isLoadingComments, setIsLoadingComments] = useState(true)
  const [isLiking, setIsLiking] = useState(false)
  const [isAddingComment, setIsAddingComment] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMoreComments, setHasMoreComments] = useState(false)
  const [isSettingCover, setIsSettingCover] = useState(false)
  const { user } = useAuth()

  // Real-time hooks
  const {
    likeCount,
    hasUserLiked,
    setLikeCount: setLikeCountState,
    setHasUserLiked
  } = usePhotoLikesRealtime({ photoId: photo.id, enabled: !!photo.id })

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load like count and status
        const [count, liked] = await Promise.all([
          photoInteractionsApi.getLikeCount(photo.id),
          currentUserId ? photoInteractionsApi.hasUserLiked(photo.id) : Promise.resolve(false)
        ])
        setLikeCountState(count)
        setHasUserLiked(liked)

        // Load comments
        setIsLoadingComments(true)
        const commentsResponse = await photoInteractionsApi.getComments(photo.id, 1, 10)
        setComments(commentsResponse.comments)
        setHasMoreComments(commentsResponse.hasMore)
      } catch (error) {
      } finally {
        setIsLoadingComments(false)
      }
    }

    if (photo.id) {
      loadData()
    }
  }, [photo.id, currentUserId, photoInteractionsApi, setLikeCountState, setHasUserLiked])

  // Real-time comment updates
  usePhotoCommentsRealtime({
    photoId: photo.id,
    enabled: !!photo.id,
    onCommentAdded: (newComment) => {
      // Check if comment already exists
      setComments(prev => {
        if (prev.some(c => c.id === newComment.id)) return prev
        return [...prev, newComment as PhotoComment]
      })
    },
    onCommentUpdated: (updatedComment) => {
      setComments(prev =>
        prev.map(c => c.id === updatedComment.id ? updatedComment as PhotoComment : c)
      )
    },
    onCommentDeleted: (commentId) => {
      setComments(prev => prev.filter(c => c.id !== commentId))
    }
  })

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft' && currentIndex > 0 && onNavigate) {
        onNavigate(currentIndex - 1)
      } else if (e.key === 'ArrowRight' && currentIndex < photos.length - 1 && onNavigate) {
        onNavigate(currentIndex + 1)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose, currentIndex, photos.length, onNavigate])

  const handleLike = useCallback(async () => {
    if (isLiking || !currentUserId) return

    try {
      setIsLiking(true)
      const optimisticLiked = !hasUserLiked
      const optimisticCount = optimisticLiked ? likeCount + 1 : Math.max(0, likeCount - 1)

      // Optimistic update
      setHasUserLiked(optimisticLiked)
      setLikeCountState(optimisticCount)

      // API call
      if (optimisticLiked) {
        await photoInteractionsApi.likePhoto(photo.id)
      } else {
        await photoInteractionsApi.unlikePhoto(photo.id)
      }
    } catch (error) {
      // Revert optimistic update
      setHasUserLiked(!hasUserLiked)
      setLikeCountState(likeCount)
    } finally {
      setIsLiking(false)
    }
  }, [photo.id, hasUserLiked, likeCount, isLiking, currentUserId, photoInteractionsApi, setHasUserLiked, setLikeCountState])

  const handleAddComment = useCallback(async (commentText: string) => {
    if (isAddingComment || !commentText.trim() || !currentUserId) return

    try {
      setIsAddingComment(true)
      
      // Optimistically add comment to local state immediately
      const optimisticComment: PhotoComment = {
        id: `temp-${Date.now()}`,
        photoId: photo.id,
        userId: currentUserId,
        userName: user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'You',
        comment: commentText.trim(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
      
      setComments(prev => [...prev, optimisticComment])
      
      // Make API call
      const newComment = await photoInteractionsApi.addComment(photo.id, commentText)
      
      // Replace optimistic comment with real one
      setComments(prev => 
        prev.map(c => c.id === optimisticComment.id ? newComment : c)
      )
    } catch (error) {
      // Remove optimistic comment on error
      setComments(prev => prev.filter(c => !c.id.startsWith('temp-')))
      throw error
    } finally {
      setIsAddingComment(false)
    }
  }, [photo.id, isAddingComment, currentUserId, photoInteractionsApi, user])

  const handleEditComment = useCallback(async (commentId: string, commentText: string) => {
    try {
      await photoInteractionsApi.updateComment(photo.id, commentId, commentText)
      // Real-time hook will update it
    } catch (error) {
      throw error
    }
  }, [photo.id, photoInteractionsApi])

  const handleDeleteComment = useCallback(async (commentId: string) => {
    try {
      await photoInteractionsApi.deleteComment(photo.id, commentId)
      // Real-time hook will remove it
    } catch (error) {
      throw error
    }
  }, [photo.id, photoInteractionsApi])

  const handlePrevious = () => {
    if (currentIndex > 0 && onNavigate) {
      onNavigate(currentIndex - 1)
    }
  }

  const handleNext = () => {
    if (currentIndex < photos.length - 1 && onNavigate) {
      onNavigate(currentIndex + 1)
    }
  }

  const handleSetCover = async () => {
    if (!onSetCoverPhoto) return
    try {
      setIsSettingCover(true)
      await onSetCoverPhoto(photo.id)
    } finally {
      setIsSettingCover(false)
    }
  }

  const canNavigatePrevious = currentIndex > 0
  const canNavigateNext = currentIndex < photos.length - 1

  return (
    <div
      className={cn('fixed inset-0 z-50 bg-black/90 flex items-center justify-center', className)}
      onClick={onClose}
    >
      {/* Close button and actions */}
      <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
        {onSetCoverPhoto && (
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              handleSetCover()
            }}
            disabled={isSettingCover}
          >
            {coverPhotoId === photo.id ? 'Cover Photo' : (isSettingCover ? 'Setting...' : 'Set as Cover')}
          </Button>
        )}
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Navigation arrows */}
      {photos.length > 1 && (
        <>
          {canNavigatePrevious && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handlePrevious()
              }}
              className="absolute left-4 z-10 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
              aria-label="Previous photo"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}
          {canNavigateNext && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleNext()
              }}
              className="absolute right-4 z-10 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
              aria-label="Next photo"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}
        </>
      )}

      {/* Content container */}
      <div
        className="w-full h-full max-w-7xl mx-auto flex flex-col md:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Photo panel - full width on mobile, 60% on desktop */}
        <div className="w-full md:w-[60%] h-[60%] md:h-full flex items-center justify-center bg-black p-4">
          {photo.url ? (
            <img
              src={getOptimizedFullSizeUrl(photo.url, photo.optimizationStatus)}
              alt={photo.caption || photo.filename || 'Photo'}
              className="max-w-full max-h-full object-contain"
              loading="eager"
              decoding="async"
              onError={(e) => {
                // Fallback to original URL if .webp version doesn't exist
                const target = e.target as HTMLImageElement
                const originalUrl = photo.url
                if (target.src !== originalUrl) {
                  target.src = originalUrl
                }
              }}
            />
          ) : (
            <div className="text-white text-center">
              <p>Photo not available</p>
            </div>
          )}
        </div>

        {/* Interaction panel - bottom sheet on mobile, side panel on desktop */}
        <div className="w-full md:w-[40%] h-[40%] md:h-full bg-white flex flex-col rounded-t-2xl md:rounded-none shadow-lg md:shadow-none">
          {/* Drag handle indicator on mobile */}
          <div className="md:hidden flex justify-center pt-2 pb-1">
            <div className="w-12 h-1 bg-gray-300 rounded-full" />
          </div>

          {/* Like button and counts */}
          <div className="border-b border-gray-200 p-4">
            <div className="flex items-center gap-4">
              <button
                onClick={handleLike}
                disabled={isLiking || !currentUserId}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
                  hasUserLiked
                    ? 'text-red-600 hover:bg-red-50'
                    : 'text-gray-600 hover:bg-gray-100',
                  isLiking && 'opacity-50 cursor-not-allowed'
                )}
              >
                {isLiking ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Heart className={cn('w-5 h-5', hasUserLiked && 'fill-red-600')} />
                )}
                <span className="text-sm font-medium">{likeCount}</span>
              </button>
            </div>

            {/* Photo info */}
            {photo.caption && (
              <div className="mt-3">
                <p className="text-sm text-gray-900">{photo.caption}</p>
              </div>
            )}
          </div>

          {/* Comments section */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <CommentSection
              comments={comments}
              currentUserId={currentUserId}
              isLoading={isAddingComment}
              onAddComment={handleAddComment}
              onEditComment={handleEditComment}
              onDeleteComment={handleDeleteComment}
              className="h-full"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
